import { Box, Button, CardMedia, Typography } from "@mui/material";
import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import granja from "../../image/Land_vacas.png";
import { setInformacion } from "../../store/slices/usuarios";
import { Alerta } from "../ux/Alert";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc.js";
import { recolectarSevice } from "../../services/recolectarSevice";
import { rutas } from "../../rutas/rutas";
import { getCookie } from "../../utils/getCookies";
import { misionService } from "../../services/misionService";
import { tierras } from "../niveles/tierras";
import { Construccion } from "./Construccion";
import { SubirNivelButton } from "./botones/SubirNivelButton";
import { Contador } from "../comun/Contador";
import { v4 as uuid } from "uuid";
import PetBenefitDisplay from "./PetBenefitDisplay";

dayjs.extend(utc);

const agua = "https://res.cloudinary.com/devuvyuij/image/upload/v1680463588/CITYFARMING/vaca_plzywc.png";

/* =========================
   UI PRO FX (SOLO VISUAL)
   - NO cambia layout ni lógica
   ========================= */

const uiFx = {
  "@keyframes glowPulse": {
    "0%": { filter: "drop-shadow(0 0 0 rgba(255,215,0,0))" },
    "50%": { filter: "drop-shadow(0 0 14px rgba(255,215,0,.20))" },
    "100%": { filter: "drop-shadow(0 0 0 rgba(255,215,0,0))" },
  },
  "@keyframes floatSlow": {
    "0%": { transform: "translateY(0px)" },
    "50%": { transform: "translateY(-6px)" },
    "100%": { transform: "translateY(0px)" },
  },
  "@keyframes shimmerBtn": {
    "0%": { transform: "translateX(-120%) skewX(-12deg)", opacity: 0 },
    "15%": { opacity: 0.22 },
    "35%": { opacity: 0.10 },
    "100%": { transform: "translateX(220%) skewX(-12deg)", opacity: 0 },
  },
  "@keyframes softFlicker": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.95 },
  },
  "@keyframes scanline": {
    "0%": { transform: "translateY(-120%)", opacity: 0 },
    "15%": { opacity: 0.16 },
    "50%": { opacity: 0.10 },
    "100%": { transform: "translateY(120%)", opacity: 0 },
  },

  // Polvo/polen suave (granja)
  "@keyframes dustDrift": {
    "0%": { transform: "translateY(0px) translateX(0px)", opacity: 0 },
    "10%": { opacity: 0.18 },
    "100%": { transform: "translateY(540px) translateX(70px)", opacity: 0 },
  },
  "@keyframes dustDrift2": {
    "0%": { transform: "translateY(0px) translateX(0px)", opacity: 0 },
    "10%": { opacity: 0.14 },
    "100%": { transform: "translateY(560px) translateX(-90px)", opacity: 0 },
  },
};

const rootFxSx = {
  ...uiFx,
  position: "relative",
  width: "100%",
  height: "100%",
  overflow: "hidden",
  filter: "saturate(1.05) contrast(1.02)",
  "&::before": {
    // sun glow + vignette
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    background:
      "radial-gradient(60% 45% at 55% 18%, rgba(255,220,140,0.10), transparent 60%)," +
      "radial-gradient(120% 90% at 50% 60%, rgba(0,0,0,0.18), rgba(0,0,0,0.52))",
    mixBlendMode: "screen",
    zIndex: 0,
  },
  "&::after": {
    // grain retro suave
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    backgroundImage:
      "url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"160\" height=\"160\"><filter id=\"n\"><feTurbulence type=\"fractalNoise\" baseFrequency=\"0.8\" numOctaves=\"3\" stitchTiles=\"stitch\"/></filter><rect width=\"160\" height=\"160\" filter=\"url(%23n)\" opacity=\"0.22\"/></svg>')",
    opacity: 0.085,
    mixBlendMode: "overlay",
    zIndex: 0,
  },
};

const scanlineFxSx = {
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
  zIndex: 1,
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(transparent 0%, rgba(255,255,255,0.03) 50%, transparent 100%)",
    opacity: 0.18,
    animation: "scanline 5.8s linear infinite",
  },
};

const panelFxSx = {
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 18px 40px rgba(0,0,0,.30)",
  transform: "translateZ(0)",
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    background:
      "radial-gradient(60% 45% at 50% 15%, rgba(255,255,255,0.06), transparent 55%)," +
      "linear-gradient(180deg, rgba(255,220,140,0.08), transparent 35%, rgba(0,0,0,0.14))",
  },
};

const invTextSx = {
  color: "white !important",
  fontSize: "0.7rem !important",
  textAlign: "start",
  mt: 1,
  textShadow: "0 0 10px rgba(0,0,0,.35)",
};

const titleSx = {
  display: "block",
  fontSize: "1.5rem !important",
  textAlign: "center !important",
  color: "white !important",
  textShadow:
    "0 0 10px rgba(255,255,255,0.16), 0 0 18px rgba(255,215,0,0.16), 0 8px 16px rgba(0,0,0,.22)",
  animation: "softFlicker 2.8s ease-in-out infinite",
  letterSpacing: "1px",
};

const lvlUpSx = {
  fontSize: "1rem !important",
  color: "greenyellow !important",
  textShadow: "0 0 14px rgba(173,255,47,.22)",
  animation: "glowPulse 3.4s ease-in-out infinite",
};

const btnFxSx = {
  position: "relative",
  overflow: "hidden",
  transition: "transform .12s ease, filter .12s ease",
  "&:hover": { transform: "translateY(-1px) scale(1.01)", filter: "brightness(1.08)" },
  "&:active": { transform: "translateY(0px) scale(0.98)", filter: "brightness(0.98)" },
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: "42%",
    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.20), transparent)",
    animation: "shimmerBtn 2.9s ease-in-out infinite",
    pointerEvents: "none",
  },
};

function DustFX({ density = 12 }) {
  const items = useMemo(() => Array.from({ length: density }), [density]);

  return (
    <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 1 }}>
      {items.map((_, idx) => {
        const left = Math.round(Math.random() * 100);
        const delay = (Math.random() * 4).toFixed(2);
        const dur = (4 + Math.random() * 5).toFixed(2);
        const size = 2 + Math.round(Math.random() * 3);
        const anim = idx % 2 === 0 ? "dustDrift" : "dustDrift2";
        return (
          <Box
            key={idx}
            sx={{
              position: "absolute",
              top: -30,
              left: `${left}%`,
              width: size,
              height: size,
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 30% 30%, rgba(255,240,200,0.18), rgba(255,220,140,0.06))",
              boxShadow: "0 0 10px rgba(255,220,140,0.10)",
              opacity: 0,
              animation: `${anim} ${dur}s linear ${delay}s infinite`,
            }}
          />
        );
      })}
    </Box>
  );
}

/* =========================
   COMPONENTE (MISMA LÓGICA)
   ========================= */

export const Vaca = ({ selector, handleClose: handleClose2 }) => {
  const [semillas, setSemillas] = useState(1);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const dispatch = useDispatch();
  const { informacion } = useSelector((state) => state.userSlice);

  const handleClose = () => setOpen(false);
  const handleChange = (e) => {
    const cantidad = parseInt(e.target.value);
    // if(cantidad > selector?.nivel)return
    setSemillas(cantidad);
  };

  const recolectarSemilla = async () => {
    try {
      const { data } = await recolectarSevice(selector?.id);
      dispatch(setInformacion(data));
      setError(false);
      setMensaje("SUCCESS");
      setOpen(true);
    } catch ({ response }) {
      setError(true);
      setOpen(true);
      setMensaje(response.data.error);
    }
  };

  const alimentar = async () => {
    try {
      const { data } = await misionService(selector?.id, 2);
      if (data?.error) {
        setError(true);
        setOpen(true);
        setMensaje(data?.error);
        return;
      }
      dispatch(setInformacion(data));
      setError(false);
      setMensaje("SUCCESS");
      setOpen(true);
    } catch ({ response }) {
      setError(true);
      setOpen(true);
      setMensaje(response.data.error);
    }
  };

  const activarAgua = async () => {
    try {
      const token = getCookie("token");
      const { data } = await axios({
        url: rutas.activarAgua,
        data: { id: 9 },
        method: "post",
        headers: {
          token: token,
        },
      });

      dispatch(setInformacion(data));
      setError(false);
      setMensaje("SUCCESS");
      setOpen(true);
    } catch (error) {
      setError(true);
      setOpen(true);
      const msg = error?.response?.data?.error || "ERROR";
      setMensaje(msg);
    }
  };

  // Solo visual: pulso en Collect cuando está listo (MISMA condición que ya usas)
  const canCollect = dayjs().utc().diff(selector?.tiempo, "second") > 0;

  /* =========================
     ✅ NUEVO: MENÚ DE NIVELES (IMAGEN GRANDE + THUMBS + REQUISITOS + BOTÓN SOLO EN NEXT)
     - NO cambia lógica, solo UI del panel izquierdo
     ========================= */

  const COW_LEVEL_IMAGES = useMemo(
    () => [
      "https://res.cloudinary.com/dpwv380jp/image/upload/v1767280654/lvl1_kpvrhn.png",
    "https://res.cloudinary.com/dpwv380jp/image/upload/v1767280655/lvl2_xbatel.png",
    "https://res.cloudinary.com/dpwv380jp/image/upload/v1767280669/lvl3_ngb7zk.png",
    "https://res.cloudinary.com/dpwv380jp/image/upload/v1767280670/lvl4_skdphg.png",
    "https://res.cloudinary.com/dpwv380jp/image/upload/v1767280671/lvl5_gmfkfr.png",
    ],
    [granja]
  );

  const landCfg = useMemo(() => tierras.find((t) => t.id === selector?.id), [selector?.id]);

  const [previewLevel, setPreviewLevel] = useState(selector?.nivel || 1);

  useEffect(() => {
    setPreviewLevel(selector?.nivel || 1);
  }, [selector?.id, selector?.nivel]);

  const maxLevel = COW_LEVEL_IMAGES?.length || 5;
  const nextLevelReal = Math.min((selector?.nivel || 1) + 1, maxLevel);

  const reqForPreviewLevel = useMemo(() => {
    if (!landCfg?.nivel) return null;
    if (previewLevel <= 1) return null;
    const idx = previewLevel - 2; // lvl2->0, lvl3->1...
    return landCfg.nivel[idx] || null;
  }, [landCfg, previewLevel]);

  return (
    <Box sx={rootFxSx}>
      {/* FX overlays - NO cambian layout */}
      <DustFX density={12} />
      <Box sx={scanlineFxSx} />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative !important",
          overflow: "auto !important",
          width: "100%",
          height: "100%",
          zIndex: 2,
        }}
      >
        <Construccion selector={selector} />

        <PetBenefitDisplay
          type="farm"
          icon="🐄"
          label="Milk production time"
        />

        {/* Close: misma ubicación (corrijo 'abolute' -> absolute para que funcione) */}
        <Box
          onClick={handleClose2}
          sx={{
            position: "absolute",
            right: 14,
            top: 10,
            zIndex: 10001,
            color: "white",
            cursor: "pointer",
            fontSize: "14px",
            textShadow: "0 0 10px rgba(0,0,0,.7)",
            opacity: 0.85,
            transition: "all .12s ease",
            "&:hover": { opacity: 1, transform: "scale(1.06)" },
          }}
        >
          X
        </Box>

        <Box class="rpgui-container framed-golden" sx={{ position: "relative !important", ...panelFxSx }}>
          <Typography variant="p" sx={invTextSx}>
            Inventory:
          </Typography>
          <Typography variant="p" sx={invTextSx}>
            Wheat bag:{" "}
            <span style={{ color: "#ffd27f" }}>
              {informacion?.inventario.find((i) => i?.item === "Wheat Bag")?.cantidad}
            </span>
          </Typography>
          <Typography variant="p" sx={invTextSx}>
            Water:{" "}
            <span style={{ color: "#7fe7ff" }}>
              {informacion?.inventario.find((i) => i?.item === "Water")?.cantidad}
            </span>
          </Typography>
          <Typography variant="p" sx={invTextSx}>
            Milk:{" "}
            <span style={{ color: "#ffffff" }}>
              {informacion?.inventario.find((i) => i?.item === "Milk")?.cantidad}
            </span>
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: "100%",
            flexDirection: { xs: "column", md: "row" },
            overflow: "auto !important",
            width: "100%",
            height: "100%",
          }}
        >
          <Box
            class="rpgui-container framed-golden"
            sx={{
              position: "relative !important",
              width: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              flexDirection: "column",
              height: "100%",
              overflow: "auto",
              ...panelFxSx,
              pt: 2,
              gap: 1,
            }}
          >
            {/* ✅ Imagen grande del nivel seleccionado */}
            <Box sx={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center", px: 1 }}>
              <CardMedia
                className="rpgui-cursor-grab-open"
                component="img"
                image={COW_LEVEL_IMAGES?.[Math.max(0, (previewLevel || 1) - 1)]}
                sx={{
                  width: "66%",
                  maxHeight: 280,
                  objectFit: "contain",
                  m: "0 auto",
                  filter: "drop-shadow(0 16px 10px rgba(0,0,0,.30))",
                  animation: "floatSlow 4.2s ease-in-out infinite",
                  userSelect: "none",
                }}
              />
            </Box>

            {/* ✅ Menú inferior con todos los niveles */}
            <Box
              sx={{
                width: "100%",
                px: 1.5,
                display: "flex",
                gap: 1,
                overflowX: "auto",
                pb: 0.5,
                "&::-webkit-scrollbar": { height: 6 },
                "&::-webkit-scrollbar-thumb": {
                  background: "rgba(255,255,255,0.18)",
                  borderRadius: 999,
                },
              }}
            >
              {Array.from({ length: maxLevel }).map((_, i) => {
                const lvl = i + 1;
                const isActive = lvl === previewLevel;
                const isOwned = lvl <= (selector?.nivel || 1);
                const isNext = lvl === nextLevelReal;

                return (
                  <Box
                    key={lvl}
                    onClick={() => setPreviewLevel(lvl)}
                    className="rpgui-cursor-point"
                    sx={{
                      minWidth: 64,
                      height: 54,
                      borderRadius: 1,
                      border: isActive ? "2px solid rgba(173,255,47,0.85)" : "1px solid rgba(255,255,255,0.18)",
                      boxShadow: isActive ? "0 0 18px rgba(173,255,47,0.18)" : "0 8px 18px rgba(0,0,0,0.22)",
                      position: "relative",
                      overflow: "hidden",
                      cursor: "pointer",
                      flex: "0 0 auto",
                      transform: isActive ? "translateY(-1px)" : "translateY(0px)",
                      transition: "all .12s ease",
                      "&:hover": { filter: "brightness(1.08)" },
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={COW_LEVEL_IMAGES?.[i]}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        opacity: isOwned ? 1 : 0.78,
                      }}
                    />

                    {/* Badge LVL */}
                    <Box
                      sx={{
                        position: "absolute",
                        left: 4,
                        top: 4,
                        px: 0.7,
                        py: 0.15,
                        borderRadius: 0.8,
                        background: "rgba(0,0,0,0.55)",
                        color: "white",
                        fontSize: "0.65rem",
                        textShadow: "0 0 10px rgba(0,0,0,.45)",
                      }}
                    >
                      LVL {lvl}
                    </Box>

                    {/* Estado */}
                    {isNext && (
                      <Box
                        sx={{
                          position: "absolute",
                          right: 4,
                          bottom: 4,
                          px: 0.6,
                          py: 0.1,
                          borderRadius: 0.8,
                          background: "rgba(173,255,47,0.18)",
                          border: "1px solid rgba(173,255,47,0.35)",
                          color: "greenyellow",
                          fontSize: "0.62rem",
                        }}
                      >
                        NEXT
                      </Box>
                    )}

                    {!isOwned && !isNext && (
                      <Box
                        sx={{
                          position: "absolute",
                          right: 4,
                          bottom: 4,
                          px: 0.6,
                          py: 0.1,
                          borderRadius: 0.8,
                          background: "rgba(0,0,0,0.45)",
                          border: "1px solid rgba(255,255,255,0.12)",
                          color: "rgba(255,255,255,0.85)",
                          fontSize: "0.62rem",
                        }}
                      >
                        LOCK
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* ✅ Requisitos + botón */}
            <Box
              sx={{
                width: "100%",
                px: 2,
                pb: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.8,
              }}
            >
              <Typography variant="p" sx={lvlUpSx}>
                +LVL UP{" "}
                <div style={{ backgroundSize: "100%", width: "30px", height: "30px" }} class="rpgui-icon exclamation" />
              </Typography>

              {reqForPreviewLevel ? (
                <Box sx={{ width: "100%" }}>
                  <Typography
                    variant="p"
                    sx={{
                      fontSize: "0.72rem !important",
                      color: "white !important",
                      textShadow: "0 0 10px rgba(0,0,0,.35)",
                      opacity: 0.9,
                    }}
                  >
                    Requisitos para llegar a{" "}
                    <span style={{ color: "#d6ff6b" }}>LVL {previewLevel}</span>:
                  </Typography>

                  {Object.entries(reqForPreviewLevel).map(([k, v]) => (
                    <Typography
                      key={`${k}-${v}`}
                      variant="p"
                      sx={{
                        fontSize: "0.7rem !important",
                        textAlign: "start",
                        color: "white !important",
                        textShadow: "0 0 10px rgba(0,0,0,.35)",
                      }}
                    >
                      {`${k}: ${v}`}
                    </Typography>
                  ))}
                </Box>
              ) : (
                <Typography
                  variant="p"
                  sx={{
                    fontSize: "0.72rem !important",
                    color: "rgba(255,255,255,0.85) !important",
                    textShadow: "0 0 10px rgba(0,0,0,.35)",
                    textAlign: "center",
                  }}
                >
                  Selecciona un nivel mayor para ver requisitos.
                </Typography>
              )}

              {/* ✅ Botón SOLO cuando seleccionas el siguiente nivel real */}
              {previewLevel === nextLevelReal && nextLevelReal > (selector?.nivel || 1) ? (
                <Box sx={{ mt: 1, "& .rpgui-button": btnFxSx }}>
                  <SubirNivelButton setError={setError} setMensaje={setMensaje} setOpen={setOpen} selector={selector} />
                </Box>
              ) : (
                <Typography
                  variant="p"
                  sx={{
                    fontSize: "0.72rem !important",
                    color: "rgba(255,255,255,0.75) !important",
                    textShadow: "0 0 10px rgba(0,0,0,.35)",
                    textAlign: "center",
                  }}
                >
                  Para subir, selecciona el nivel{" "}
                  <span style={{ color: "#d6ff6b" }}>NEXT</span>.
                </Typography>
              )}
            </Box>
          </Box>

          <Box
            class="rpgui-container framed-golden"
            sx={{
              position: "relative !important",
              ml: 1,
              width: "50%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              overflow: "auto",
              ...panelFxSx,
            }}
          >
            <Typography variant="p" sx={titleSx}>
              Cow Farm
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "start", width: "100%", height: "100%", flexDirection: "column" }}>
              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", ml: 1 }}>
                <CardMedia
                  className="rpgui-cursor-grab-open"
                  component="img"
                  image={agua}
                  sx={{
                    width: "90px",
                    objectFit: "cover",
                    filter: "drop-shadow(0 16px 10px rgba(0,0,0,.30))",
                    animation: "glowPulse 3.0s ease-in-out infinite",
                  }}
                />
              </Box>

              <Typography variant="p" sx={{ fontSize: "1rem !important", textAlign: "start", mt: 1, color: "white !important", textShadow: "0 0 10px rgba(0,0,0,.35)" }}>
                LVL: <span style={{ color: "#d6ff6b" }}>{selector?.nivel}</span>
              </Typography>
              <Typography variant="p" sx={{ fontSize: "1rem !important", textAlign: "start", mt: 1, color: "white !important", textShadow: "0 0 10px rgba(0,0,0,.35)" }}>
                Production: <span style={{ color: "#ffffff" }}>2 liters of milk</span> x level
              </Typography>
              <Typography variant="p" sx={{ fontSize: "1rem !important", textAlign: "start", mt: 1, color: "white !important", textShadow: "0 0 10px rgba(0,0,0,.35)" }}>
                Duration: 24 hours
              </Typography>
              <Typography variant="p" sx={{ fontSize: "1rem !important", textAlign: "start", mt: 1, color: "white !important", textShadow: "0 0 10px rgba(0,0,0,.35)" }}>
                Requirements: <span style={{ color: "#ffd27f" }}>2 bag wheat feed</span> x level;{" "}
                <span style={{ color: "#7fe7ff" }}>3 water</span> x level
              </Typography>

              <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
                {selector?.tiempo === "" && (
                  <Button onClick={alimentar} class="rpgui-button" sx={btnFxSx}>
                    <p>Feed</p>
                  </Button>
                )}

                {dayjs().utc().diff(selector?.tiempo, "second") > 0 && (
                  <Button
                    class="rpgui-button"
                    onClick={recolectarSemilla}
                    sx={{
                      ...btnFxSx,
                      animation: canCollect ? "glowPulse 1.8s ease-in-out infinite" : "none",
                    }}
                  >
                    <p>Collect</p>
                  </Button>
                )}
              </Box>

              {selector?.tiempo !== "" && (
                <Box sx={{ mt: 1, animation: canCollect ? "glowPulse 2.2s ease-in-out infinite" : "softFlicker 2.8s ease-in-out infinite" }}>
                  <Contador segundos={selector?.tiempo} />
                </Box>
              )}
            </Box>

            <Alerta mensaje={mensaje} open={open} handleClose={handleClose} error={error} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
